(() => {
  if (customElements.get('tpl-one-time-bundle-tote')) return;

  class OneTimeBundleTote extends HTMLElement {
    static plan(cart, bundleId, toteId) {
      const items = cart.items || [];
      const quantity = items.reduce((total, item) =>
        total + (String(item.variant_id || item.id) === bundleId && !item.selling_plan_allocation
          ? Number(item.quantity) : 0), 0);
      const gifts = items.filter(item => String(item.variant_id || item.id) === toteId
        && item.properties?._tpl_one_time_bundle_tote === '1');
      const updates = {};
      gifts.forEach((item, index) => {
        const desired = index === 0 ? quantity : 0;
        if (item.quantity !== desired) updates[item.key] = desired;
      });
      return { quantity, gifts, updates, add: quantity > 0 && gifts.length === 0 };
    }

    static covered(cart, plan, toteId) {
      if (plan.quantity === 0) return true;
      const expected = plan.gifts.reduce((total, gift) => total + Number(gift.original_line_price), 0);
      const allocated = (cart.items || []).filter(item => String(item.variant_id || item.id) === toteId)
        .reduce((total, item) => total + (item.line_level_discount_allocations || [])
          .filter(allocation => allocation.discount_application?.title === 'Complimentary tote with your care bundle')
          .reduce((amount, allocation) => amount + Number(allocation.amount), 0), 0);
      return Number.isFinite(expected) && expected > 0 && allocated >= expected;
    }

    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.root = this.dataset.root || '/';
      this.onCartUpdate = event => {
        if (event.source !== 'tpl-one-time-bundle-tote') this.queue().catch(() => {});
      };
      this.onCheckout = event => {
        const target = event.target.closest?.('button[name="checkout"], a[href*="/checkout"]');
        if (!target || this.bypassCheckout) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.queue().then(() => {
          const checkout = document.getElementById(target.id) ||
            (target.isConnected ? target : document.querySelector('button[name="checkout"]'));
          this.bypassCheckout = true;
          checkout?.click();
          this.bypassCheckout = false;
        }).catch(() => {});
      };
      this.onSubmit = event => {
        if (!event.submitter?.matches('[name="checkout"]') || this.bypassCheckout) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.queue().then(() => {
          const form = document.getElementById(event.target.id) || event.target;
          const submitter = document.getElementById(event.submitter.id) || form.querySelector('[name="checkout"]');
          this.bypassCheckout = true;
          form.requestSubmit(submitter);
          this.bypassCheckout = false;
        }).catch(() => {});
      };
      document.addEventListener('click', this.onCheckout, true);
      document.addEventListener('submit', this.onSubmit, true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);
      }
      this.queue().catch(() => {});
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      document.removeEventListener('click', this.onCheckout, true);
      document.removeEventListener('submit', this.onSubmit, true);
      this.started = false;
    }

    lock(value) {
      this.lockedButtons ||= new Map();
      this.lockedExpress ||= new Map();
      if (value) {
        document.querySelectorAll('button[name="checkout"]').forEach(button => {
          if (!this.lockedButtons.has(button)) this.lockedButtons.set(button, button.disabled);
          button.disabled = true;
        });
        document.querySelectorAll('shopify-accelerated-checkout-cart').forEach(element => {
          if (!this.lockedExpress.has(element)) this.lockedExpress.set(element, element.inert);
          element.inert = true;
        });
      } else {
        this.lockedButtons.forEach((disabled, button) => { button.disabled = disabled; });
        this.lockedExpress.forEach((inert, element) => { element.inert = inert; });
        this.lockedButtons.clear();
        this.lockedExpress.clear();
      }
    }

    queue() {
      this.rerun = true;
      if (this.running) return this.running;
      this.lock(true);
      this.running = (async () => {
        do {
          this.rerun = false;
          await this.sync();
        } while (this.rerun);
        this.hidden = true;
        this.textContent = '';
        this.lock(false);
      })().catch(error => {
        this.textContent = this.dataset.error;
        this.hidden = false;
        throw error;
      }).finally(() => { this.running = null; });
      return this.running;
    }

    async request(path, body) {
      const options = { credentials: 'same-origin', headers: { Accept: 'application/json' } };
      if (body) {
        options.method = 'POST';
        options.headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(body);
      } else options.cache = 'no-store';
      const response = await fetch(this.root + path, options);
      const data = await response.json();
      if (!response.ok || data.errors) throw new Error(data.description || this.dataset.error);
      return data;
    }

    sections() {
      const cart = document.querySelector('cart-items');
      return cart?.getSectionsToRender?.() || [];
    }

    async sync() {
      let cart = await this.request('cart.js');
      const plan = OneTimeBundleTote.plan(cart, this.dataset.bundle, this.dataset.tote);
      const sections = this.sections();
      const render = {
        sections: sections.map(section => section.section),
        sections_url: window.location.pathname
      };
      let changed;
      if (Object.keys(plan.updates).length) {
        changed = await this.request('cart/update.js', { updates: plan.updates, ...render });
      }
      if (plan.add) {
        changed = await this.request('cart/add.js', {
          items: [{ id: Number(this.dataset.tote), quantity: plan.quantity,
            properties: { _tpl_one_time_bundle_tote: '1' } }], ...render
        });
      }
      if (changed) cart = await this.request('cart.js');
      const verified = OneTimeBundleTote.plan(cart, this.dataset.bundle, this.dataset.tote);
      const quantity = verified.gifts.reduce((total, gift) => total + gift.quantity, 0);
      if (quantity !== verified.quantity || !OneTimeBundleTote.covered(cart, verified, this.dataset.tote)) {
        if (verified.gifts.length) {
          const rollback = await this.request('cart/update.js', {
            updates: Object.fromEntries(verified.gifts.map(gift => [gift.key, 0])), ...render
          });
          this.render(rollback, rollback.sections, sections);
        }
        throw new Error(this.dataset.error);
      }
      if (changed) {
        this.render(cart, changed.sections, sections);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tpl-one-time-bundle-tote', cartData: cart });
        }
      }
    }

    render(cart, markup, sections) {
      if (!markup) {
        window.location.reload();
        return;
      }
      sections.forEach(section => {
        if (!markup[section.section]) return;
        const html = new DOMParser().parseFromString(markup[section.section], 'text/html');
        const target = document.getElementById(section.id);
        const source = html.querySelector(section.selector);
        const contents = target?.querySelector(section.selector) || target;
        if (contents && source) contents.innerHTML = source.innerHTML;
      });
      document.querySelectorAll('cart-items, cart-drawer, #main-cart-footer').forEach(element => {
        element.classList.toggle('is-empty', cart.item_count === 0);
      });
      this.lock(true);
    }
  }

  customElements.define('tpl-one-time-bundle-tote', OneTimeBundleTote);
})();
